import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  VStack,
  useToast,
} from '@chakra-ui/react';
import SelectForm from 'components/SelectForm';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SaveFavoriteAccountBody, createAccount, updateAccount, validatePixKey } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { onlyNumbers } from 'utils/string';

export interface FavoriteAccount {
  id: string;
  accountHolder: string | null;
  accountHolderDocument?: string | null;
  pix: string | null;
  agency: string | null;
  account: string | null;
  bankIspb: string | null;
  accountType?: string | null;
}

interface FormValues {
  name: string;
  document: string;
  pix: string;
  agency: string;
  account: string;
  bankIspb: string;
  type: string;
}

type Method = 'PIX' | 'BANK';

const emptyValues: FormValues = {
  name: '',
  document: '',
  pix: '',
  agency: '',
  account: '',
  bankIspb: '',
  type: '',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Quando presente, o modal abre em modo de edição. */
  favorite?: FavoriteAccount | null;
}

export default function FavoriteFormModal({ isOpen, onClose, onSaved, favorite }: Props) {
  const toast = useToast();
  const isEdit = !!favorite;
  const [method, setMethod] = useState<Method>('PIX');
  const [loading, setLoading] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: emptyValues });

  // Preenche o formulário ao abrir para edição (ou limpa ao abrir para cadastro).
  useEffect(() => {
    if (!isOpen) return;
    if (favorite) {
      const hasBank = !favorite.pix && !!(favorite.agency || favorite.account || favorite.bankIspb);
      setMethod(hasBank ? 'BANK' : 'PIX');
      reset({
        name: favorite.accountHolder || '',
        document: favorite.accountHolderDocument || '',
        pix: favorite.pix || '',
        agency: favorite.agency || '',
        account: favorite.account || '',
        bankIspb: favorite.bankIspb || '',
        type: favorite.accountType || '',
      });
    } else {
      setMethod('PIX');
      reset(emptyValues);
    }
  }, [isOpen, favorite, reset]);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      let body: SaveFavoriteAccountBody;

      if (method === 'PIX') {
        const pix = values.pix?.trim();
        if (!pix) {
          toast({ title: 'Aviso', description: 'Informe a chave PIX', status: 'warning', duration: 3000, isClosable: true });
          setLoading(false);
          return;
        }
        // Valida a chave antes de salvar; se resolver, confirma/preenche titular e documento.
        let name = values.name?.trim();
        let document = onlyNumbers(values.document || '');
        try {
          const consulted = await validatePixKey(pix);
          if (consulted) {
            if (!name && consulted.name) name = consulted.name;
            if (!document && consulted.document) document = onlyNumbers(consulted.document);
            toast({
              title: 'Chave PIX válida',
              description: consulted.name ? `Titular: ${consulted.name}` : 'Chave validada',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        } catch {
          toast({
            title: 'Aviso',
            description: 'Não foi possível validar a chave PIX, mas o favorecido será salvo assim mesmo',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
        }
        body = { pix, name: name || undefined, document: document || undefined, isFavorite: true };
      } else {
        const account = values.account?.trim();
        if (!account) {
          toast({ title: 'Aviso', description: 'Informe o número da conta', status: 'warning', duration: 3000, isClosable: true });
          setLoading(false);
          return;
        }
        body = {
          name: values.name?.trim() || undefined,
          document: onlyNumbers(values.document || '') || undefined,
          agency: values.agency?.trim() || undefined,
          account,
          bankIspb: values.bankIspb?.trim() || undefined,
          type: values.type || undefined,
          isFavorite: true,
        };
      }

      if (isEdit && favorite) {
        await updateAccount(favorite.id, body);
      } else {
        await createAccount(body);
      }

      toast({
        title: 'Sucesso',
        description: isEdit ? 'Favorecido atualizado' : 'Favorecido cadastrado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onSaved();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao salvar favorecido',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Editar favorecido' : 'Cadastrar favorecido'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <RadioGroup value={method} onChange={(v) => setMethod(v as Method)} isDisabled={isEdit}>
              <Stack direction="row" spacing={6}>
                <Radio value="PIX">Chave PIX</Radio>
                <Radio value="BANK">Dados bancários</Radio>
              </Stack>
            </RadioGroup>

            <FormControl>
              <FormLabel fontSize="sm" mb="8px">
                Nome
              </FormLabel>
              <Input size="sm" bg="white" rounded="sm" placeholder="Nome do favorecido" {...register('name')} />
            </FormControl>

            <FormControl isInvalid={!!errors.document?.message}>
              <FormLabel fontSize="sm" mb="8px">
                Documento
              </FormLabel>
              <Input
                size="sm"
                bg="white"
                rounded="sm"
                placeholder="CPF ou CNPJ"
                {...register('document', {
                  validate: (value) => {
                    if (!value) return true;
                    const digits = onlyNumbers(value);
                    return validCpf(digits) || validCnpj(digits) || 'Documento inválido';
                  },
                })}
              />
              <FormErrorMessage>{errors.document?.message}</FormErrorMessage>
            </FormControl>

            {method === 'PIX' ? (
              <FormControl>
                <FormLabel fontSize="sm" mb="8px">
                  Chave PIX
                </FormLabel>
                <Input
                  size="sm"
                  bg="white"
                  rounded="sm"
                  placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                  {...register('pix')}
                />
              </FormControl>
            ) : (
              <>
                <SelectForm
                  control={control}
                  label="Tipo de conta"
                  name="type"
                  placeholder="Selecione o tipo"
                  options={[
                    { label: 'Conta Corrente', value: 'CACC' },
                    { label: 'Conta de Pagamento', value: 'TRAN' },
                    { label: 'Conta Poupança', value: 'SVGS' },
                  ]}
                />
                <HStack spacing={3} align="start">
                  <FormControl>
                    <FormLabel fontSize="sm" mb="8px">
                      Agência
                    </FormLabel>
                    <Input size="sm" bg="white" rounded="sm" placeholder="0001" {...register('agency')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" mb="8px">
                      Conta (com dígito)
                    </FormLabel>
                    <Input size="sm" bg="white" rounded="sm" placeholder="00101" {...register('account')} />
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="sm" mb="8px">
                    Banco (ISPB)
                  </FormLabel>
                  <Input size="sm" bg="white" rounded="sm" placeholder="Ex: 60701190" {...register('bankIspb')} />
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancelar
          </Button>
          <Button colorScheme="green" onClick={handleSubmit(onSubmit)} isLoading={loading}>
            {isEdit ? 'Salvar' : 'Cadastrar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
